import { useCallback, type CSSProperties, type ReactElement } from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { IconButton, useViewport } from "@primitives/index";
import { ShellFrame } from "../workflow-layout/ShellFrame";
import { TopBar } from "../workflow-layout/TopBar";
import { BackToWorkflows, ChromeActions } from "../workflow-layout/ChromeActions";
import { SettingsSideNav } from "./SettingsSideNav";
import { SettingsNavBar } from "./SettingsNavBar";
import { SETTINGS_CONTENT_MAX_WIDTH, SETTINGS_NAV_WIDTH, SETTINGS_ROOT } from "./settingsNav";

/**
 * SettingsLayout: layout route for the per-user settings pages.
 *
 * Settings are per user, so this layout is a sibling of `AdminLayout`, not a
 * child. One chrome (top bar + account menu), then the category nav in the
 * shape the viewport class calls for:
 *
 *  - `expanded`+  two columns: a sticky side nav and a centered content
 *                 column capped at `SETTINGS_CONTENT_MAX_WIDTH`.
 *  - `medium`     a horizontal pill bar under the top bar, content below.
 *  - `compact`    list first. `/settings` shows the category list; a category
 *                 page shows a back control to that list in the top bar.
 */

const SPLIT_GAP_PX = 48;

const splitStyle: CSSProperties = {
    width: "100%",
    maxWidth: SETTINGS_NAV_WIDTH + SPLIT_GAP_PX + SETTINGS_CONTENT_MAX_WIDTH,
    marginInline: "auto",
    display: "grid",
    gridTemplateColumns: `${String(SETTINGS_NAV_WIDTH)}px minmax(0, 1fr)`,
    columnGap: SPLIT_GAP_PX,
    alignItems: "start",
    minWidth: 0,
};

const columnStyle: CSSProperties = {
    width: "100%",
    maxWidth: SETTINGS_CONTENT_MAX_WIDTH,
    marginInline: "auto",
    minWidth: 0,
};

function BackToSettings(): ReactElement {
    const navigate = useNavigate();
    const handleBack = useCallback(() => void navigate(SETTINGS_ROOT), [navigate]);
    return (
        <IconButton
            icon={ArrowLeft}
            aria-label="Back to settings"
            tooltip="Back to settings"
            onClick={handleBack}
        />
    );
}

export function SettingsLayout(): ReactElement {
    const { viewport, isCompact, isAtLeast } = useViewport();
    const atRoot = useMatch({ path: SETTINGS_ROOT, end: true }) !== null;
    const leading = isCompact && !atRoot ? <BackToSettings /> : <BackToWorkflows />;

    return (
        <ShellFrame
            topBar={<TopBar leading={leading} actions={<ChromeActions />} />}
            subBar={viewport === "medium" ? <SettingsNavBar /> : undefined}
            content="scroll"
        >
            {isAtLeast("expanded") ? (
                <div style={splitStyle} data-testid="settings-split">
                    <SettingsSideNav />
                    <div style={columnStyle}>
                        <Outlet />
                    </div>
                </div>
            ) : (
                <div style={columnStyle}>
                    <Outlet />
                </div>
            )}
        </ShellFrame>
    );
}
